import { Button, Form, Modal } from "antd";
import { message } from "utils/ciq-message";
import { useContext, useEffect, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { gql } from "@apollo/client";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { MUTATION_CREATE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { MaterialExtractSpecType } from "../model";
import MaterialFormBody from "./material-form-body";
import "./create-material-model.scss";

type CreateMaterialModalProps = {
  open: boolean;
  onCancel: () => void;
  onComplete: () => void;
  selectedRow: MaterialExtractSpecType;
  materialHeaderMap: any;
};

export default function CreateMaterialModal({
  open,
  onCancel,
  onComplete,
  selectedRow,
  materialHeaderMap
}: CreateMaterialModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedSpecIds, setSelectedSpecIds] = useState<string[]>([]);

  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);

  const [createEntityMutation] = useCIQMutation(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),
    { client: gqlClientForProject }
  );

  useEffect(() => {
    if (open && selectedRow) {
      form.setFieldsValue({
        title: selectedRow.title || "",
        tag_name: selectedRow.tag || "",
        manufacturer: selectedRow.manufacturer || "",
        description: ""
      });
      setSelectedSpecIds([]);
    }
  }, [open, selectedRow, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const featureInstance: any = {
        extraction_data_id: selectedRow.id,
        title: values.title,
        tag_name: values.tag_name,
        manufacturer: values.manufacturer,
        description: values.description,
        spec_sections: selectedSpecIds
      };

      if (values.assignee) {
        featureInstance.assignee = values.assignee;
      }
      if (values.gc_representative) {
        featureInstance.gc_representative = values.gc_representative;
      }

      const variables = {
        feature_type: "MATERIALS",
        feature_instances: [featureInstance]
      };

      const response: any = await createEntityMutation({ variables });

      if (response?.data?.insert_feature_instances_multi?.ids?.length) {
        message.success("Material was added to the log.");
        onComplete();
      } else {
        message.error("Unable to create Material.");
      }
    } catch (error) {
      console.error("Error creating material:", error);
    }
    setLoading(false);
  };

  return (
    <Modal
      className="create-material-modal"
      title={
        <span className="text-base font-bold text-ciq-gray-950-a90">
          Create Material
        </span>
      }
      open={open}
      width={500}
      centered
      destroyOnHidden
      onCancel={onCancel}
      footer={[
        <section className="flex justify-end gap-2" key="footer">
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={loading}
            onClick={handleSubmit}
          >
            Confirm &amp; Create
          </Button>
        </section>
      ]}
    >
      <div className="overflow-y-auto max-h-[450px]">
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          style={{ paddingTop: -50 }}
          validateTrigger="onSubmit"
        >
          <MaterialFormBody
            form={form}
            materialHeaderMap={materialHeaderMap}
            selectedSpecIds={selectedSpecIds}
            setSelectedSpecIds={setSelectedSpecIds}
            token={tokenRetrievalState?.token}
          />
        </Form>
      </div>
    </Modal>
  );
}
